/**
 * StripePaymentElement – Inline Stripe Payment Element component.
 *
 * Renders an embedded Stripe Payment Element (cards, Apple Pay, Google Pay,
 * Link, and all other methods enabled on the account) directly on the page
 * without any redirect to Stripe Checkout.
 *
 * Props:
 *  - clientSecret  {string}   – The PaymentIntent client_secret from the backend.
 *  - projectId     {string}   – Theme generation ID (for confirm-payment call).
 *  - amount        {number}   – Amount in cents (displayed to the user).
 *  - onSuccess     {function} – Called when payment succeeds; triggers download.
 *  - onCancel      {function} – Called when the user dismisses the element.
 */

import React, { useState } from 'react';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Typography,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import axios from 'axios';

// Load Stripe once, outside of any component render cycle.
// @stripe/stripe-js fetches the Stripe.js script automatically.
//
// The publishable key is read from the REACT_APP_STRIPE_PUBLISHABLE_KEY
// environment variable, which is baked into the bundle at build time by CRA.
// For local development set it in src/frontend/.env.local (never commit).
// For Railway/nixpacks deployments set STRIPE_PUBLISHABLE_KEY in the Railway
// dashboard – nixpacks.toml bridges it to REACT_APP_STRIPE_PUBLISHABLE_KEY
// automatically before the build runs.
const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
if (!STRIPE_PK) {
  // eslint-disable-next-line no-console
  console.error(
    '[StripePaymentElement] REACT_APP_STRIPE_PUBLISHABLE_KEY is not set.\n' +
    '  • Local dev: add it to src/frontend/.env.local\n' +
    '  • Railway:   set STRIPE_PUBLISHABLE_KEY in the Railway dashboard\n' +
    '    (nixpacks.toml will forward it automatically as REACT_APP_STRIPE_PUBLISHABLE_KEY)'
  );
}
const stripePromise = loadStripe(STRIPE_PK);

// ─── Inner form rendered inside <Elements> ───────────────────────────────────

function CheckoutForm({ projectId, amount, onSuccess, onCancel, returnUrl }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setError(null);

    // confirmPayment with redirect:'if_required' keeps the user on-page for
    // cards, Apple Pay, Google Pay, etc. Only wallet-redirect methods will
    // leave the page (Stripe handles the redirect + return automatically).
    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: returnUrl || `${window.location.origin}/projects/${projectId}?payment=success`,
      },
      redirect: 'if_required',
    });

    if (stripeError) {
      setError(stripeError.message || 'Payment failed. Please try again.');
      setSubmitting(false);
      return;
    }

    if (paymentIntent && paymentIntent.status === 'succeeded') {
      // Frontend-driven confirmation: tell the backend the payment succeeded
      try {
        await axios.post(
          `/api/billing/confirm-payment/${projectId}`,
          { paymentIntentId: paymentIntent.id },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
      } catch (confirmErr) {
        // Non-fatal: webhook will also mark it paid
        console.warn('Frontend confirm-payment call failed (webhook will handle, but download may be delayed):', confirmErr.message);
      }

      // Trigger download automatically
      onSuccess(paymentIntent);
    } else {
      // Unexpected status (e.g. requires_action handled by redirect)
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
      <PaymentElement
        options={{
          layout: 'tabs',
          wallets: { applePay: 'auto', googlePay: 'auto' },
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={!stripe || submitting}
          startIcon={
            submitting ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <LockIcon />
            )
          }
          sx={{ flex: 1 }}
        >
          {submitting
            ? 'Processing…'
            : `Pay $${(amount / 100).toFixed(2)}`}
        </Button>

        {onCancel && (
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </Button>
        )}
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        align="center"
        sx={{ mt: 1 }}
      >
        Secure payment powered by Stripe
      </Typography>
    </Box>
  );
}

// ─── Public component: wraps CheckoutForm with the <Elements> provider ───────

export default function StripePaymentElement({ clientSecret, projectId, amount, onSuccess, onCancel, returnUrl }) {
  const appearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: '#1976d2',
      borderRadius: '4px',
    },
  };

  return (
    <Elements
      stripe={stripePromise}
      options={{ clientSecret, appearance }}
    >
      <CheckoutForm
        projectId={projectId}
        amount={amount}
        onSuccess={onSuccess}
        onCancel={onCancel}
        returnUrl={returnUrl}
      />
    </Elements>
  );
}
